import React, { memo } from "react"
import { Link } from "gatsby"

/**
 * S1 — the hinge (LANDING-PLAN §4).
 *
 * HANDOFF: the hero leaves the reader asking whether their situation is one we
 * mean. This answers by putting eight of them on the page in their own words,
 * each one clickable.
 *
 * Eight ICP rows, hairline separated. No cards and no fills — eight cards would
 * read as a wall, and this is a list to scan. The icons are retained from the
 * earlier card treatment; the card chrome is not.
 *
 * Every symptom is written fresh but its substance comes from the `symptoms`
 * arrays in solutions.js, or from the ICP list for the three rows that have no
 * offer page yet. No new claim or capability is introduced
 * (CONTENT-ARCHITECTURE §0).
 *
 * Rows 3, 6 and 8 have no offer page and route to /contact. Row 6 moves to
 * /offers/erp-rescue-sprint once that page ships.
 */

const IconDoc = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
)

const IconLayers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
)

const IconStore = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9l1.5-5h15L21 9" />
    <path d="M4 9v11h16V9" />
    <path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" />
  </svg>
)

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)

const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
)

const IconAlert = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
)

const IconCode = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
)

const ROWS = [
  {
    icon: IconDoc,
    label: "Installation & field service",
    symptom:
      "You find out a job lost money when the invoice goes out. Nothing between the order and the invoice told you sooner.",
    to: "/offers/field-service-installer-platform",
  },
  {
    icon: IconLayers,
    label: "Manufacturing",
    symptom: "Your system says the stock is there. The floor says otherwise.",
    to: "/offers/odoo-for-manufacturers",
  },
  {
    icon: IconStore,
    label: "Multi-store retail & franchise",
    symptom: "Every store reports differently and head office reconciles by hand.",
    // The symptom is this offer's own, near verbatim: "Month-end close takes
    // weeks because every store reports differently" (offers.js, offer 09).
    to: "/offers/odoo-for-multi-store-retail",
  },
  {
    icon: IconCalendar,
    label: "Private practices & clinics",
    symptom: "Your front desk spends the day on scheduling calls.",
    to: "/offers/practice-and-telehealth-platform",
  },
  {
    icon: IconPhone,
    label: "Home services & trades",
    symptom: "The call you missed at six was booked with someone else by seven.",
    to: "/offers/ai-voice-receptionist",
  },
  {
    icon: IconAlert,
    label: "An ERP that never landed",
    symptom: "The system went live. The spreadsheets never went away.",
    // ERP Rescue Sprint is exactly this reader: "Businesses with a stalled,
    // abandoned or underperforming ERP", whose symptoms include "Your team has
    // gone back to spreadsheets while the system sits half-configured".
    to: "/offers/erp-rescue-sprint",
  },
  {
    icon: IconCode,
    label: "Product engineering capacity",
    symptom: "Your roadmap is set but you do not have the engineers to build it.",
    // Embedded Delivery Pod, whose first symptom is "Your roadmap needs a team,
    // not a project" — not Development Month, which is one discipline for a
    // month rather than a team against a roadmap.
    to: "/offers/embedded-delivery-pod",
  },
]

/**
 * Instrumentation — CONTENT-ARCHITECTURE §7 / G5. Each row reports which ICP
 * was chosen. Uses the gtag already loaded in Layout; silently does nothing if
 * analytics is blocked or absent.
 *
 * THE /contact FALLBACKS ARE GONE. Three rows — multi-store retail, the stalled
 * ERP, and engineering capacity — used to point at /contact because their offer
 * pages did not exist yet, and the click data was there to decide which to build
 * first. All eighteen offers are live and built now, so every row resolves to
 * the offer that matches its own symptom. The tracking stays; the fallback has
 * nothing left to decide.
 */
const trackHinge = label => {
  if (typeof window === "undefined") return
  if (typeof window.gtag === "function") {
    window.gtag("event", "hinge_row_click", { icp_label: label })
  }
}

/**
 * Pattern A — asymmetric split with a numbered rail.
 *
 * The heading sits BESIDE the list, not above it, and the left column is
 * sticky. That is the whole mechanism: S1 runs well over a viewport, so a
 * non-sticky heading is visible for row 01 and gone for the other seven, and
 * the section spends its largest type in the first screenful and then has no
 * anchor at all. Sticky is what makes a 110px heading worth having here.
 */
const StartWhereYouAre = () => (
  <section className="hs-start">
    <div className="hs-start__inner">
      <div className="hs-start__aside">
        <div className="hs-start__aside-inner">
          <span className="hs-start__eyebrow">Start here</span>
          <h2 className="hs-start__heading">Which of these is you?</h2>
          <p className="hs-start__lede">
            Pick the line that matches your week. Each one leads to what gets
            built, how long it takes, and what it costs.
          </p>
          <Link to="/offers/" className="hs-start__cta">
            See every offer &rarr;
          </Link>
        </div>
      </div>

      <ul className="hs-start__list">
        {ROWS.map((r, i) => {
          const Icon = r.icon
          return (
            <li
              key={r.label}
              className="hs-start__row"
              // NO transitionDelay. Each row carries its own [data-reveal] and
              // is observed independently, so scroll position already staggers
              // them — the inline delay stacked a second, artificial cascade on
              // top of that. At seven rows it reached 420ms on row 06, which
              // opened a window where one row sat at 30% opacity while the rows
              // either side of it were solid. That reads as a missing row and a
              // white gap, not as an animation, and it is worst scrolling UP:
              // row 07 reveals first, so row 06 is the one left blank.
              data-reveal
            >
              <Link
                to={r.to}
                className="hs-start__link"
                onClick={() => trackHinge(r.label)}
              >
                {/* §6.2 numeric rail. A second visual axis for the section
                    that costs no card, border, radius or new token, and lets
                    the reader see the list is finite before reading it —
                    which is most of what a hinge is for. Decorative, so it is
                    hidden from assistive tech; the label and symptom carry
                    the meaning. */}
                <span className="hs-start__num" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="hs-start__icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="hs-start__body">
                  <span className="hs-start__label">{r.label}</span>
                  <span className="hs-start__symptom">{r.symptom}</span>
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  </section>
)

export default memo(StartWhereYouAre)
